import { getSurveyAnswers } from "src/hooks/survey/answers";
import { trackEvent } from "src/utils/api/tracker";
import {
  getBMI,
  getEligibilityFailuresBySku,
  getEligibleMedProducts,
  getHighestBMI,
  getMedicalHealthRisks,
  getMedState,
} from "src/utils/medEligibility";
import { MED_FLOW, MED_SKU } from "src/utils/medSku";
import {
  checkPDQuestionsEligibility,
  getPersonalizedDosingEligibility,
} from "src/utils/userSegment/features";

export type MedEligibilityCheckpoint =
  | "bmi_early"
  | "bmi_full"
  | "health_risk"
  | "zipcode"
  | "pd_questions"
  | "plan_load"
  | "checkout";

type CheckpointOptions = Parameters<typeof getEligibleMedProducts>[0];

const CLINICAL_DISABLED_NAMED_CHECKS: CheckpointOptions["disabledNamedChecks"] =
  ["isQualifiedForPD", "isNotEligibleForCompoundedSemaglutide"];

/**
 * Mirrors the options used by the segment that gates each checkpoint in
 * src/utils/userSegment/features.ts, so the tracked verdict matches the one
 * the user actually experienced. `undefined` means "default options", the same
 * as a bare getEligibleMedProducts() call.
 */
const CHECKPOINT_OPTIONS: Record<
  MedEligibilityCheckpoint,
  CheckpointOptions | undefined
> = {
  // isBMINoomClinicalEligibleBeforePrevMeds
  bmi_early: {
    checkBMI: true,
    checkHealthRisks: false,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: false,
    disabledNamedChecks: CLINICAL_DISABLED_NAMED_CHECKS,
  },
  // isBMINoomClinicalEligible
  bmi_full: {
    checkBMI: true,
    checkHealthRisks: false,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: true,
    requireHighestBMIAnswer: true,
    disabledNamedChecks: CLINICAL_DISABLED_NAMED_CHECKS,
  },
  // isEligibleForMedAfterHealthRiskQuestion
  health_risk: {
    checkBMI: true,
    checkHealthRisks: true,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: true,
    requireHighestBMIAnswer: true,
    disabledNamedChecks: CLINICAL_DISABLED_NAMED_CHECKS,
  },
  // isEligibleForMedAfterZipcodeQuestion
  zipcode: {
    checkBMI: true,
    checkHealthRisks: true,
    checkState: true,
    checkFlow: true,
    requirePreviousMedicationsAnswer: true,
    requireHighestBMIAnswer: true,
    disabledNamedChecks: CLINICAL_DISABLED_NAMED_CHECKS,
  },
  // getEligibleMedProducts() with defaults, as used when loading plans and
  // by isEligibleForMedProducts. Unlike the checkpoints above, these also
  // enforce the PD and compounded-semaglutide named checks.
  pd_questions: undefined,
  plan_load: undefined,
  checkout: undefined,
};

const lastEmittedByCheckpoint = new Map<MedEligibilityCheckpoint, string>();

type TrackOptions = {
  recommendedMedTier?: MED_SKU;
  eligibleMedTiers?: MED_SKU[];
  extra?: JsonObject;
};

/**
 * Personalized dosing inputs. Only the payment survey collects the PD
 * answers, and isQualifiedForPD is enforced by the default-option checkpoints.
 */
function getPersonalizedDosingProperties(
  answers: ReturnType<typeof getSurveyAnswers>
) {
  const { isEligible, reason } = getPersonalizedDosingEligibility();
  return {
    qualified: isEligible,
    reason: reason ?? null,
    qualifiedByQuestions: checkPDQuestionsEligibility(),
    answers: {
      pdFatigue: answers.pdFatigue ?? null,
      pdLifting: answers.pdLifting ?? null,
      pdGastrointestinalDiscomfort:
        answers.pdGastrointestinalDiscomfort ?? null,
      pdGastrointestinalIssues: answers.pdGastrointestinalIssues ?? null,
      pdSideEffectConcern: answers.pdSideEffectConcern ?? null,
      pdNauseaImpact: answers.pdNauseaImpact ?? null,
    },
  };
}

function finiteOrNull(value: number) {
  return Number.isFinite(value) ? Math.round(value * 10) / 10 : null;
}

/**
 * Emits one MedEligibilityEvaluated event with the input data points the
 * eligibility rules read and the resulting eligible products.
 *
 * Deliberately not called from getEligibleMedProducts(): that runs on every
 * segment evaluation/render. Call it at a checkpoint instead. Re-emitting the
 * same checkpoint with identical data is a no-op.
 *
 * Privacy: sends the derived state (never the zipcode) and BMI (never height
 * or weight).
 */
export function trackMedEligibilityCheckpoint(
  checkpoint: MedEligibilityCheckpoint,
  { recommendedMedTier, eligibleMedTiers, extra }: TrackOptions = {}
) {
  const options = CHECKPOINT_OPTIONS[checkpoint];
  const eligibleProducts = getEligibleMedProducts(options);
  const answers = getSurveyAnswers();

  // The diagnostics only describe the base per-SKU rules, so they are skipped
  // for the default-option checkpoints, where route-specific wrappers apply.
  const failedChecksBySku = options
    ? getEligibilityFailuresBySku(options, MED_FLOW.CLINICAL)
    : undefined;
  const failedChecks = failedChecksBySku
    ? Array.from(new Set(Object.values(failedChecksBySku).flat())).sort()
    : undefined;

  const properties: JsonObject = {
    checkpoint,
    eligible: eligibleProducts.length > 0,
    eligibleProducts,
    checksApplied: options
      ? {
          bmi: options.checkBMI,
          healthRisks: options.checkHealthRisks,
          state: options.checkState,
          flow: options.checkFlow,
        }
      : "default",
    ...(failedChecksBySku && !eligibleProducts.length && { failedChecks }),
    ...(failedChecksBySku && { failedChecksBySku }),
    inputs: {
      bmi: finiteOrNull(getBMI()),
      highestBmi: finiteOrNull(getHighestBMI()),
      state: getMedState() ?? null,
      previousMedications: answers.previousMedications ?? null,
      whichMedication: answers.whichMedication ?? null,
      healthRisks: getMedicalHealthRisks(),
      ageRange: answers.ageRange ?? null,
      pregnant: answers.pregnant ?? null,
      eatingDisorder: answers.eatingDisorder ?? null,
      medicare: answers.medicare ?? null,
      medicareBridgeScreening: answers.medicareBridgeScreening ?? null,
      medOptIn: answers.medOptIn ?? null,
    },
    ...(checkpoint === "pd_questions" && {
      personalizedDosing: getPersonalizedDosingProperties(answers),
    }),
    ...(recommendedMedTier && { recommendedMedTier }),
    ...(eligibleMedTiers && { eligibleMedTiers }),
    ...extra,
  };

  const fingerprint = JSON.stringify(properties);
  if (lastEmittedByCheckpoint.get(checkpoint) === fingerprint) {
    return;
  }
  lastEmittedByCheckpoint.set(checkpoint, fingerprint);

  trackEvent("MedEligibilityEvaluated", properties);
}

export function resetMedEligibilityTrackingForTests() {
  lastEmittedByCheckpoint.clear();
}
